import React from "react";
import { User } from "../../utils/api/endpoints/usersApi";
import { UserRole } from "../../utils/api/endpoints/user/credentials";
import { FiMail, FiUser, FiCheck, FiX, FiShield } from "react-icons/fi";

interface ModernUserCardProps {
  user?: User;
  variant?: "compact" | "detailed" | "search-result";
  showActions?: boolean;
  onAction?: (action: string, userId: number) => void;
  className?: string;
  clickable?: boolean;
  onClick?: () => void;
}

const ModernUserCard: React.FC<ModernUserCardProps> = ({
  user,
  variant = "detailed",
  showActions = false,
  onAction,
  className = "",
  clickable = false,
  onClick
}) => {
  if (!user) {
    return (
      <div className={`animate-pulse bg-gray-100 rounded-lg p-4 ${className}`}>
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
          <div className="flex-1 space-y-2">
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="h-3 bg-gray-200 rounded w-1/2"></div>
          </div>
        </div>
      </div>
    );
  }

  const getRoleInfo = (role: UserRole) => {
    switch (role) {
      case UserRole.RESEARCHER:
        return { label: "Researcher", color: "text-blue-600 bg-blue-50", icon: FiUser };
      case UserRole.BUSINESS:
        return { label: "Business", color: "text-green-600 bg-green-50", icon: FiShield };
      case UserRole.ADMIN:
        return { label: "Admin", color: "text-purple-600 bg-purple-50", icon: FiShield };
      default:
        return { label: "User", color: "text-gray-600 bg-gray-50", icon: FiUser };
    }
  };

  const getStatusInfo = (approved: boolean) => {
    return approved
      ? { label: "Active", color: "text-green-700 bg-green-100", icon: FiCheck }
      : { label: "Inactive", color: "text-red-700 bg-red-100", icon: FiX };
  };

  const roleInfo = getRoleInfo(user.role);
  const statusInfo = getStatusInfo(user.approved);
  const RoleIcon = roleInfo.icon;
  const StatusIcon = statusInfo.icon;

  const displayName = user.displayName || user.username || "Unknown User";
  const email = user.email || "No email provided";

  // Get initials for avatar fallback
  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map(word => word.charAt(0))
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const baseClasses = `
    bg-white border border-gray-200 rounded-lg transition-all duration-200
    ${clickable ? "hover:shadow-md hover:border-gray-300 cursor-pointer" : ""}
    ${className}
  `;

  if (variant === "compact") {
    return (
      <div className={`${baseClasses} p-3`} onClick={onClick}>
        <div className="flex items-center space-x-3">
          {/* Avatar */}
          <div className="relative flex-shrink-0">
            {user.profilePicture ? (
              <img
                src={user.profilePicture}
                alt={displayName}
                className="w-10 h-10 rounded-full object-cover"
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-medium text-sm">
                {getInitials(displayName)}
              </div>
            )}
            {/* Status indicator */}
            <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white ${user.approved ? 'bg-green-400' : 'bg-red-400'}`}></div>
          </div>

          {/* User info */}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">{displayName}</p>
            <p className="text-xs text-gray-500 truncate">{email}</p>
          </div>

          {/* Role badge */}
          <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${roleInfo.color}`}>
            <RoleIcon className="w-3 h-3 mr-1" />
            {roleInfo.label}
          </div>

          {/* Actions */}
          {showActions && onAction && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onAction('remove', user.id);
              }}
              className="p-1.5 rounded-lg hover:bg-red-100 transition-colors group flex-shrink-0"
              title="Remove user"
            >
              <FiX className="w-4 h-4 text-gray-400 group-hover:text-red-500" />
            </button>
          )}
        </div>
      </div>
    );
  }

  if (variant === "search-result") {
    return (
      <div className={`${baseClasses} p-4 hover:bg-gray-50`} onClick={onClick}>
        <div className="flex items-center space-x-4">
          {/* Avatar */}
          <div className="relative flex-shrink-0">
            {user.profilePicture ? (
              <img
                src={user.profilePicture}
                alt={displayName}
                className="w-12 h-12 rounded-full object-cover"
              />
            ) : (
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-medium">
                {getInitials(displayName)}
              </div>
            )}
          </div>

          {/* User info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-2">
              <h3 className="text-base font-semibold text-gray-900 truncate">{displayName}</h3>
              {user.approved && (
                <FiCheck className="w-4 h-4 text-green-500 flex-shrink-0" />
              )}
            </div>
            <div className="flex items-center space-x-1 mt-1">
              <FiMail className="w-3 h-3 text-gray-400" />
              <p className="text-sm text-gray-600 truncate">{email}</p>
            </div>
            <div className="flex items-center space-x-3 mt-2">
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${roleInfo.color}`}>
                <RoleIcon className="w-3 h-3 mr-1" />
                {roleInfo.label}
              </span>
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${statusInfo.color}`}>
                <StatusIcon className="w-3 h-3 mr-1" />
                {statusInfo.label}
              </span>
            </div>
          </div>

          {/* Action indicator */}
          <div className="flex-shrink-0">
            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
              <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Default detailed variant
  return (
    <div className={`${baseClasses} p-4`} onClick={onClick}>
      <div className="flex items-center space-x-4">
        {/* Avatar */}
        <div className="relative flex-shrink-0">
          {user.profilePicture ? (
            <img
              src={user.profilePicture}
              alt={displayName}
              className="w-14 h-14 rounded-full object-cover"
            />
          ) : (
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-semibold text-lg">
              {getInitials(displayName)}
            </div>
          )}
          {/* Status indicator */}
          <div className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-white ${user.approved ? 'bg-green-400' : 'bg-red-400'}`}></div>
        </div>

        {/* User info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-2">
            <h3 className="text-lg font-semibold text-gray-900 truncate">{displayName}</h3>
            {user.approved && (
              <FiCheck className="w-5 h-5 text-green-500 flex-shrink-0" />
            )}
          </div>
          <div className="flex items-center space-x-1 mt-1">
            <FiMail className="w-4 h-4 text-gray-400" />
            <p className="text-sm text-gray-600 truncate">{email}</p>
          </div>
          <div className="flex items-center space-x-3 mt-3">
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${roleInfo.color}`}>
              <RoleIcon className="w-4 h-4 mr-1" />
              {roleInfo.label}
            </span>
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${statusInfo.color}`}>
              <StatusIcon className="w-4 h-4 mr-1" />
              {statusInfo.label}
            </span>
          </div>
        </div>

        {/* Actions */}
        {showActions && onAction && (
          <div className="flex-shrink-0 flex space-x-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onAction('remove', user.id);
              }}
              className="p-2 rounded-lg hover:bg-red-100 transition-colors group"
              title="Remove user"
            >
              <FiX className="w-4 h-4 text-gray-400 group-hover:text-red-500" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ModernUserCard;
